/**
 * All available feature flags in the monorepo.
 * Add new flags here to make them available across all apps.
 */
export const featureFlagNames = [
  'ABT_AdditionalNasaFields',
  'ABT_AutoSendCOIMessage',
  // MET/ABT "Firm or flexible target date" dropdown (CID-7268). Only hunkware-api stores the answer
  // (HunkWare_Node_Version_Save_Quote) and prints it into the quote notes (HunkWare_Node_Version_Calculate_Quote); on
  // the php-api path the answer is simply dropped.
  'ABT_Firm_Flexible_Target_Date',
  // Off = php-api, on = hunkware-api. Wire shape is identical either way.
  // Docs: docs/migrations/locations-availability/07-frontend-migration.md
  'ABT_Franchise_Partners_Accessing',
  // MET/ABT garage / attic / basement address factors (CID-7267) — shows and sends them on its own. Only hunkware-api
  // stores them (HunkWare_Node_Version_Save_Quote) and prints them into the quote notes
  // (HunkWare_Node_Version_Calculate_Quote); on the php-api path the answers are simply dropped.
  'ABT_Garage_Attic_Basement',
  'ABT_KeepHigherNumberHunks',
  'ABT_OSE_Reasons',
  'ABT_Partner_Node_Migration',
  'ABT_Price_Calendar_Indicators',
  'ABT_RemoveNotesAddInstructions',
  'ABT_ShowAdminFeeInSummary',
  'ABT_ShowViewFPAvailability',
  'ABT_UpdateAutoGeneratedNotes',
  'ABT_UpdatedConfirmationCopy',
  'Automation_Allow0DaysSetting',
  'Automation_Local30MinDelay',
  'Automations_AllowMultipleJobCategories',
  'Automations_AllowStateSelection',
  // Read by the boards automations executor, php-api and its port alike
  // (docs/migrations/boards-automations-cron/04-data-inputs.md §Split).
  'Automations_DebugDoNotCallTwilio',
  'Automations_EmailAttachmentsOnGlobalAutomations',
  'Automations_EmailTemplateHTMLPreview',
  'Automations_EnableEmailABTesting',
  // Read by the boards automations executor (docs/migrations/boards-automations-cron/04-data-inputs.md §Split).
  'Automations_ExcludeAutomationsFromWonSwimlane',
  'Automations_FranchiseGroups',
  // php-api evaluates it on every inbound message (messages.post.php:298); ported by the message-webhook-phone
  // migration (docs/migrations/message-webhook-phone/04-data-inputs.md §Feature flags).
  'Automations_MarkDoNotContactOnReply',
  'Automations_ScheduleOutboundCall',
  // Transport flag for the attachment-types php-api → hunkware-api migration.
  // Distinct from 'CrewApp-AtachmentTypes' below, which gates the UI type selector — see
  // docs/migrations/attachment-types/07-frontend-migration.md.
  'Crew_App_AttachmentTypes_Node_Migration',
  // Transport flag for the locations-teams migration's crew-app callers
  // (docs/migrations/locations-teams/07-frontend-migration.md).
  'Crew_App_Location_Teams_Node_Migration',
  'Crew_App_WorkForce_Scheduling_Integration',
  'CrewApp_AllowMultipleAddendumFormSubmit',
  'CrewApp_AllowMultiplePreExistingDamageDocs',
  'CrewApp_HunkPayTipsInApp',
  'CrewApp_LiabilityAndPreExistingDamageForm',
  'CrewApp_ShowPortToPortBOL',
  'CrewApp-AtachmentTypes',
  'CrewApp-Display-Coaching-Tips',
  'CrewApp-Display-HunkswayProcess',
  'CrewAppe_Use_2025_Binder',
  'CustomerPortal_AcceptQuoteLink',
  'CustomerPortal_OnlineBooking_Cancellation_Offer',
  'CustomerPortal-OptInCommunications',
  'Document_Templates_Enabled',
  'DocumentTemplates_BlockPdfEnabled',
  'for_testing_purposes_only',
  'FranchiseSettings_AdminFeeChooseCategories',
  'FranchiseSettings_Integrations_Workforce_Scheduling',
  'FranchiseSettings-AcceptEmailEstimates',
  'FranchiseSettings-Display-HunkswayProcess',
  // Read by movesettings.get.php:368 to splice `general_time_modifier_*` into the response. The
  // misspelled "Modifer" is php's, and is the real key in Split — do NOT correct it.
  // Docs: docs/migrations/location-move-settings/04-data-inputs.md §Feature flags
  'FranchiseSettings-TimeModifer',
  // Off = php-api, on = hunkware-api for the Account-Calls epic (CID-4778). One flag for the whole
  // epic, so GET /twilio/queues (CID-7371), GET /twilio/search/calls/#phone (CID-7370) and the
  // public website call-requests route (CID-4781) move as one decision. Measured 2026-09-23: it
  // already serves 100 % on in testing, staging and training, and off in production
  // (docs/migrations/call-queues/07-frontend-migration.md).
  'HunkWare_Account_Calls_Node_Migration',
  'HunkWare_Account_Location_Core_Node_Migration',
  'HunkWare_Account_Top_Level_Node_Migration',
  'HunkWare_Account_List_v4_Migration',
  // Off = php-api, on = hunkware-api for POST /public/website/accounts/find. One flag for the whole
  // CID-7333 epic, so the account-management public surface moves as one decision rather than per
  // endpoint. customer-portal's `findAccount` is the only caller; the Node port reproduces the
  // legacy envelope bug-for-bug, including `meta.errors` being a bare STRING on a clean miss
  // (docs/migrations/public-website-accounts-find/07-frontend-migration.md).
  'HunkWare_Account_Public_Website_Node_Migration',
  // Public-website booking-wizard ACTIONS — the account ping (CID-4888): its own flag by owner decision, separate
  // from the find/PUT epic flag above, so the keep-alive ramps and rolls back on its own
  // (docs/migrations/website-account-ping/07-frontend-migration.md).
  'HunkWare_Account_Public_Website_Actions_Node_Migration',
  'HunkWare_Account_Quote_Info_Node_Migration',
  // Gates the hunkware-api scheduler that replaces the php-api crontab for GET
  // /reviews/cron/email/send (CID-7797) — no UI reads it. The crontab line is disarmed BEFORE it goes
  // on (docs/migrations/reviews-cron-email-send/07-frontend-migration.md).
  'HunkWare_Account_Reviews_Node_Migration',
  'HunkWare_Active_Directory_Migration',
  'HunkWare_Advanced_Move_Settings_Franchise_Settings',
  'HunkWare_Advanced_Pricing_Node_Migration',
  'HunkWare_Advanced_Pricing_Stacked_Modifiers',
  'HunkWare_Appointment_Attachments_Node_Migration',
  // Read by locations/appointments/reviews.put.php:314 — selects TrustPilot's native email over the
  // template-link path. The lowercase "Hunkware" is the real key in Split; do NOT re-case it.
  'Hunkware_enable_trustpilot',
  'Crew_App_Appointment_Attachments_Node_Migration',
  'HunkWare_Appointment_Communications_Follow_Ups_Node_Migration',
  // Transport switch for the appointment payments family — ONE flag for two legacy routes:
  // all four verbs of /locations/#location_id/appointments/#appointment_id/payments/#?payment_id
  // (CID-4920) and /locations/#location_id/appointments/#appointment_id/payments/link (CID-4921).
  // Off = php-api, on = hunkware-api; both Node ports reproduce the legacy envelope bug-for-bug,
  // `meta.status`'s four-key row and `credit_card.type_id`'s STRING type included, so the wire shape
  // is identical either way.
  //
  // Ramping it moves SEVEN callers at once. CID-4920: hunkware-ui's jobs payment dialog
  // (POST/PUT/DELETE) and its met-abt prepayment list (GET), plus crew-app's collect-payment page
  // (POST). CID-4921: hunkware-ui's jobs top-bar "Send payment link" and its met-abt prepayment send,
  // plus crew-app's send-payment-link page. crew-app's callers additionally stay on php-api whenever
  // Training Mode is on — there is no training hunkware-api to point at.
  // Docs: docs/migrations/location-appointment-payments/07-frontend-migration.md,
  // docs/migrations/location-appointment-payments-link/07-frontend-migration.md
  'HunkWare_Appointment_Financial_Node_Migration',
  // Transport switch for the appointment payments-and-invoicing family, owner-named (CID-7407). First rider:
  // POST /locations/#location_id/appointments/#appointment_id/payments/square — hunkware-ui's card charge
  // (`chargeCreditCardAction`). Off = php-api, on = hunkware-api; the charge ramps apart from the ledger's
  // HunkWare_Appointment_Financial_Node_Migration on purpose.
  //
  // Ramping it moves THREE callers of the same Square charge engine: that staff charge, plus the public
  // POST /public/website/locations/#location_id/accounts/#account_id/appointments/#appointment_id/payments/square
  // (CID-4960) from customer-portal's `squarePayment` and booking-v2's prepayment `createNewPayment`.
  // Docs: docs/migrations/location-appointment-payments-square/07-frontend-migration.md,
  //       docs/migrations/public-website-appointment-payments-square/07-frontend-migration.md
  'HunkWare_Appointment_Payments_And_Invoicing_Node_Migration',
  // Epic-wide transport switch for CID-7355 (Payment & Accounting Integrations), owner-named: ONE flag
  // for every migration of the epic (docs/migrations/square-locations/00-overview.md D5). First rider:
  // GET /square/locations/#location_id (CID-7554). Off = php-api, on = hunkware-api; the Node port
  // reproduces the legacy envelope, so the wire shape is identical either way.
  //
  // Ramping it moves every caller of every epic route migrated by then — today hunkware-ui's Square
  // accounts context (jobs payment form) and crew-app's two payments pages. crew-app's caller stays on
  // php-api whenever Training Mode is on — crew-app has no Training Mode URL for hunkware-api.
  // Docs: docs/migrations/square-locations/07-frontend-migration.md
  //
  // Also read SERVER-side, per request user, by hunkware-api's two QuickBooks settings gateways (CID-8046): on = the
  // in-process engine, off = php-api. Docs: docs/migrations/quickbooks-settings/00-overview.md decision 4.
  'HunkWare_Payment_Accounting_Integrations_Node_Migration',
  'HunkWare_Appointment_Reference_Data_Node_Migration',
  // Off = php-api, on = hunkware-api for the board CRUD route /locations/#location_id/boards/#?board_id — all four
  // verbs, one switch. Owner-named: board CRUD ramps with claims and resources, NOT with the
  // _Workflow_Management_ flag the /boards lookups and swimlanes ride
  // (docs/migrations/locations-boards/07-frontend-migration.md).
  'HunkWare_Boards_Claims_Resources_Node_Migration',
  // Off = php-api `POST /login`, on = hunkware-api `POST /v1/login`; the wire shape is identical either way. ONE flag for
  // the password-management family in hunkware-ui and crew-app (`/login`, `/password/reset/check`, `/password/reset`). Split
  // evaluates it on the LOGIN page, before any user or location attribute exists, so it targets environments, not
  // users. Docs: docs/migrations/login/07-frontend-migration.md
  'HunkWare_Auth_Password_Management_Node_Migration',
  'HunkWare_Boards_Workflow_Management_Node_Migration',
  'HunkWare_Appointment_Page_Switch_Layout',
  'HunkWare_Appointment_Tracking_And_Logs_Node_Migration',
  // Transport switch for the field-operations time-tracking family of php→Node migrations. Off =
  // php-api, on = hunkware-api; the wire shape is identical either way. A FAMILY flag: every caller
  // on it ramps and rolls back together. It carries
  // - GET|POST|PUT|DELETE /locations/#location_id/appointments/#appointment_id/timesheets/#?timesheet_id
  //   — hunkware-ui's four `TimesheetActions` builders (CID-7417,
  //   docs/migrations/appointment-timesheets/07-frontend-migration.md);
  // - GET|POST|PUT /locations/#location_id/appointments/#appointment_id/{lw,sdo,ped}/#?id — crew-app's
  //   liability-waiver, shipper-declaration and pre-existing-damage signature pages (CID-7419/7420/7421,
  //   docs/migrations/appointment-lw-sdo-ped/07-frontend-migration.md).
  'HunkWare_Appointment_Field_Operations_Time_Tracking_Node_Migration',
  // Transport flag for the appointment-lifecycle family, owner-named; first rider: the Team & Truck
  // tab's primary-team star, PUT …/appointments/#/primaryteam
  // (docs/migrations/appointment-primary-team/07-frontend-migration.md); second: the estimate conversion,
  // POST …/appointments/#/estimates/convert, in hunkware-ui and crew-app
  // (docs/migrations/locations-appointments-crud/07-frontend-migration.md).
  'HunkWare_Appointment_Lifecycle_Conversion_Node_Migration',
  // Off = php-api, on = the in-process engine, for the five hunkware-api gateways that reached
  // GET locations/#/appointments/#/updatetotals or the batch POST locations/#/update-totals over HTTP
  // (docs/migrations/appointment-updatetotals/05-contract-schema.md, caller reconciliation).
  'HunkWare_Appointment_Updates_Linked_Node_Migration',
  // Off = php-api, on = hunkware-api, for every caller of GET locations/#/appointments[/#] and …/appointments/date —
  // hunkware-ui, crew-app and the two hunkware-api gateways that delegated to php-api
  // (docs/migrations/locations-appointments-get/07-frontend-migration.md). Wire shape identical either way.
  'HunkWare_General_Appointment_Management_Node_Migration',
  'HunkWare_Brand_Central_Settings_V4_Pages',
  'HunkWare_Brokerage_Fee',
  'HunkWare_Calls_List_v4_Migration',
  'HunkWare_Dropdown_Management',
  'HunkWare_Field_Resources_Scheduling_Node_Migration',
  'HunkWare_Force_SSO',
  'HunkWare_Force_Users_To_New_ABT',
  'HunkWare_Force_Uses_To_Use_New_Account_Page',
  'HunkWare_Franchise_contacts_AccountList',
  'HunkWare_Franchise_Settings_Satellite_Office',
  // CID-7948: shows Franchise Settings → Integrations → AI and allows its settings API. Not a send gate.
  'HunkWare_Franchise_Settings_AI_Integrations',
  // Transport switch for the geolocation/address-services family of php→Node migrations. Off =
  // php-api, on = hunkware-api. The routes on it:
  //   GET  /geocoder                        and POST /maps/address/geocode   (the geocoding engines)
  //   GET  /public/website/geocoder                                          (its customer-token alias)
  //   POST /getpostalinfo                   and POST /public/website/getpostalinfo
  //                                         (hunkware-ui address form, franchise-settings territory
  //                                         validator, met-abt, booking-v2, booking-v4), shared with
  //                                         the public-website states lookup
  // Every Node port reproduces the legacy snake_case body, the PDO string coordinates and the meta
  // envelope bug-for-bug, so the wire shape is identical either way.
  //
  // ONE flag for the family deliberately: these routes read and warm the same `tbl_address_geocode`
  // cache through the same Google Maps gateway. Splitting them would allow a state where one surface
  // warms the cache on Node while the other reads it on php. They ramp as one unit — the flag moves
  // every caller of every route on it at once.
  // Docs: docs/migrations/geocoder/07-frontend-migration.md,
  //       docs/migrations/maps-address-geocode/07-frontend-migration.md,
  //       docs/migrations/public-website-geocoder/07-frontend-migration.md,
  //       docs/migrations/getpostalinfo/07-frontend-migration.md
  'HunkWare_Geolocation_Address_Services_Node_Migration',
  'HunkWare_Hunkpay_Payment',
  // Transport switch for the customer-messaging family of php→Node migrations (first:
  // /locations/{id}/messages/settings, GET + PUT). Off = php-api, on = hunkware-api. Wire shape is
  // identical either way — the Node endpoints reproduce the legacy snake_case body and meta envelope
  // bug-for-bug. Docs: docs/migrations/location-messages-settings/07-frontend-migration.md
  // Also gates the Node BullMQ scheduler that replaces the php crontab for
  // GET /sms/queue/appointment/confirmations (docs/migrations/sms-queue-appointment-confirmations/07-frontend-migration.md)
  // and for GET /sms/queue, the queue consumer (docs/migrations/sms-queue/07-frontend-migration.md).
  // On POST /sms/twilio/receive/confirmation it is evaluated SERVER-SIDE with the customer's reduced
  // From number as the targeting key: that route's only caller is Twilio, so there is no UI transport
  // to switch — Node owns the webhook and forwards to php-api while the flag is off. Keyed per
  // customer so a percentage ramp splits traffic and one customer's exchange stays on one
  // implementation. Docs: docs/migrations/sms-twilio-receive-confirmation/07-frontend-migration.md
  // Transport switch for POST /verify/send/sms and /verify/send/email (CID-7542/CID-7544): off = php-api,
  // on = hunkware-api (Bearer via customOptions.useNode), read by hunkware-ui and crew-app. Deliberately
  // separate from HunkWare_User_Identity_Access_Control_Node_Migration, which gates the neighbouring
  // user-identity routes in the same api modules. Docs: docs/migrations/verify-send/07-frontend-migration.md
  'HunkWare_Identity_Verification_SSO_Node_Migration',
  'HunkWare_Customer_Messaging_Node_Migration',
  // Transport switch for the CID-7395…7772 customer-messaging INBOX scope (canned responses first,
  // then messages, conversations, scheduled, options, do-not-contact and the cron). Off = php-api,
  // on = hunkware-api (Bearer via customOptions.useNode). The Node ports reproduce the legacy
  // envelope bug-for-bug, so the wire shape is identical either way.
  //
  // DELIBERATELY SEPARATE from HunkWare_Customer_Messaging_Node_Migration above, which the two
  // already-merged messaging migrations (settings CID-7401, images CID-7403) ride: the messaging
  // family now has TWO switches, and a ramp of one moves none of the other's traffic. Retrofitting
  // the merged pair onto this flag is a coupled-ramp decision for the epic owner, not this
  // migration's scope. Docs: docs/migrations/locations-messages-responses/07-frontend-migration.md
  'HunkWare_Customer_Messaging_Inbox_Conversations_Node_Migration',
  'HunkWare_Disable_Hardcoded_Scheduling_Logic',
  'HunkWare_Inventory_Tracker',
  'HunkWare_Location_Management_Core_Node_Migration',
  'Hunkware_JobPage_EnableAdminFeeOption',
  // Transport switch for the campaign-sources family: `GET /sources` and
  // `GET|POST|DELETE /locations/{id}/sources{/id}`. Off = php-api, on = hunkware-api (Bearer via
  // customOptions.useNode / config.useNode). The Node port reproduces the legacy envelope
  // bug-for-bug, so the response shape is identical either way.
  //
  // Named as a FAMILY flag by owner decision: `/locations/{id}/sources/categories` is an explicit
  // follow-on that will ride the same switch. Consequence to know before ramping — ONE flag gates
  // SEVEN callers in three families (the `entities/sources` effector store, the franchise-settings
  // campaign-management page, and the jobs + met-abt react-fetching pickers), so first-enable blast
  // radius is all of them and a rollback reverts all of them. php-web's seven call sites are NOT
  // behind it: they ride php-api until that app retires.
  //
  // IT ALSO GATES THE AUTOMATIONS FAMILY. `/automations/actions` (CID-7519) and
  // `/automations/events` (CID-7520) landed on main under this same flag name while the sources
  // migration was in flight, each with its own `node-migration` mirror. The entry was duplicated in
  // this list by that merge and is deduplicated here; the two mirrors stay separate because
  // different bridges feed them, but they read one treatment. So the real first-enable blast radius
  // is the union: the hunk-ops workflow board AND the three source-picker screens.
  //
  // It is ALSO read SERVER-side by hunkware-api, with no browser mirror: the boards automations cron
  // scheduler (CID-7771) and the event-time automation sweep (CID-8003), which the tasks and leads
  // writes run per ACTING user — off = the php-api hops (POST /tasks/{id}/automations/sweep,
  // /tasks/{id}/relations/automations, /tasks/automations/relations), on = the same sweep in process.
  // One flag per epic (CID-7353), so ramping or rolling back the screens moves the sweep with them.
  //
  // Docs: docs/migrations/sources/07-frontend-migration.md,
  // docs/migrations/automations-actions/07-frontend-migration.md,
  // docs/migrations/boards-automations-event-sweep/07-frontend-migration.md
  'HunkWare_Marketing_Automations_Lead_Sources_Node_Migration',
  'HunkWare_Taxes_Coupons_Commissions_Node_Migration',
  'HunkWare_Location_Teams_Node_Migration',
  'HunkWare_Task_Management_Core_Node_Migration',
  // Transport switch for GET/PUT/POST /locations/#location_id/appointments/#appointment_id/claims/#?claim_id
  // and its /accounts/#account_id sibling (CID-4947): the hunk-ops task-details claim panel and its
  // resolution form. Off = php-api, on = hunkware-api; the wire shape is identical either way. Its own
  // flag, not the tasks core one: the claim routes ramp and roll back on their own.
  // Docs: docs/migrations/location-claims/07-frontend-migration.md
  'HunkWare_Appointment_Misc_Public_Actions_Node_Migration',
  // Transport switch for the tasks COLLABORATION family — deliberately not the core family flag
  // above, so ramping or rolling it back moves only these callers:
  //   - GET/POST/DELETE /tasks/#task_id/watchers/#?watcher_id (CID-7462) — the watch/unwatch toggle
  //     on the hunk-ops task pages
  //   - GET/POST/DELETE /tasks/#task_id/likes/#?like_id (CID-7465) — hunkware-ui + crew-app like/unlike
  //   - GET|PUT|DELETE /tasks/#task_id/attachments/#?attachment_id (CID-7780) — the attachment delete
  //     on the hunk-ops and crew-app task pages
  // Off = php-api, on = hunkware-api; the wire shape is identical either way.
  // Docs: docs/migrations/tasks-watchers/, docs/migrations/tasks-likes/ and
  // docs/migrations/tasks-attachments/ 07-frontend-migration.md
  'HunkWare_Task_Collaboration_And_Automation_Node_Migration',
  'HunkWare_User_Identity_Access_Control_Node_Migration',
  'HunkWare_Location_Groups_Node_Migration',
  // Transport switch for the location-appointments family (CID-7391). Its first member is GET
  // /dispatchmessage — the canned-message list behind the appointment page's "Message location"
  // dialog (hunkware-ui jobs dispatch-messages context). Off = php-api, on = hunkware-api; the wire
  // shape is identical either way. Routes that join later ramp and roll back together with it.
  // Docs: docs/migrations/dispatch-messages-list/07-frontend-migration.md
  'HunkWare_Location_Appointments_Node_Migration',
  // Transport switch for the appointment-scoped reviews and schedules family (CID-7392). Its first
  // member is /locations/#location_id/appointments/#appointment_id/schedules/#?schedule_id — all
  // four verbs — and the appointment reviews routes join the same switch as they migrate. Off =
  // php-api, on = hunkware-api; the wire shape is identical either way.
  //
  // ONE flag for the family deliberately: the jobs Team & Truck tab, the Schedule calendar and the
  // ops-dashboard edit-schedule dialog each drive several of these verbs in one screen, so a
  // per-verb switch would permit a window with the list on Node and its delete on php-api. Coupled
  // ramp, shared rollback, first-enable blast radius = the union of every caller reading it.
  // Docs: docs/migrations/locations-appointments-schedules/07-frontend-migration.md
  'HunkWare_Location_Appointments_Reviews_Schedules_Node_Migration',
  'HunkWare_Location_Settings_Preferences_Node_Migration',
  // Transport switch for the public-website location-content group: GET
  // /public/website/locations/#location_id/settings/durations (CID-7577), the arrival-windows sibling
  // (CID-7576) and pricing-by-product (CID-7575). Off = php-api, on = hunkware-api; the wire shape is
  // identical either way. booking-v4 reads it (durations, arrival windows) and customer-portal reads it
  // (arrival windows); booking-v1/v3 ride php until they retire.
  //
  // ONE flag for the group (owner decision), so a rollback reverts every caller reading it. The split
  // already exists in Split: on in testing/staging/training, off in production — non-prod callers
  // move to Node as soon as this lands.
  //
  // Docs: docs/migrations/public-website-location-durations/07-frontend-migration.md
  // Docs: docs/migrations/public-website-location-arrival-windows/07-frontend-migration.md
  'HunkWare_Public_Website_Content_Config_Node_Migration',
  // Transport switch for GET/PUT /locations/#location_id/movesettings (CID-7494), POST
  // /locations/#location_id/prepayments (CID-7495) AND GET/PUT /locations/#location_id/junksettings
  // (CID-7499). Off = php-api, on = hunkware-api; the wire shape is identical either way.
  //
  // ONE flag for all three deliberately: the prepayments engine consumes the move-settings engine in
  // process (`prepayments.post.php:58`), so on the Node side the settings a prepayment is priced
  // from come from the Node port. A separate flag would allow a state where prepayments answers on
  // Node while the settings screen writes to php — same franchisee, two stores, no way to tell.
  //
  // junksettings rides this family flag but is INERT — it gates NO caller today, and that is
  // deliberate rather than an oversight. The route’s only HTTP caller is php-web’s admin/junk page,
  // and php-web is deprecated for near-future retirement, so it is never flipped (owner decision).
  // It is registered so a future hunkware-ui junk-settings screen can ramp onto the port without
  // minting a second flag — a live Split flag cannot be renamed, and an UNREGISTERED name evaluates
  // to control/off, which is a silent no-flip that looks shipped.
  //
  // The ramp is coupled: a rollback reverts every caller reading this flag, so first-enable blast
  // radius is the union of the family’s callers.
  //
  // Docs: docs/migrations/location-move-settings/07-frontend-migration.md
  //       docs/migrations/location-prepayments/07-frontend-migration.md
  //       docs/migrations/location-junk-settings/07-frontend-migration.md
  'HunkWare_Location_Operations_Financial_Config_Node_Migration',
  'HunkWare_Messaging_Paginated_Messages',
  // Off = php-api, on = hunkware-api; the wire shape is identical either way. ONE switch for the
  // pricing-zones / franchise-alerts / attachments family (owner decision) — the family's routes
  // ride it together as they migrate, starting with /locations/{id}/alerts.
  // Docs: docs/migrations/location-alerts/07-frontend-migration.md
  'HunkWare_Pricing_Zones_Alerts_Attachments_Node_Migration',
  'HunkWare_Reporting_Node_Migration',
  'HunkWare_move_labor_port_to_port_pricing',
  'HunkWare_Move_Settings_Advanced',
  'HunkWare_Partner_Node_Migration',
  'HunkWare_Partner_Phone_Number',
  // CID-7971: Performance dashboard's default range and MTD/YTD presets end on yesterday instead of today.
  'HunkWare_Perfdashboard_Previous_Day_Ranges',
  // CID-7951: Performance dashboard shows the new Metabase dashboard (251) with the new filters.
  'HunkWare_Perfdashboard_Replacement',
  'HunkWare_Print_Work_Order',
  // Transport switch for GET /locations/#location_id/pricing/audits — hunkware-ui's main-pricing
  // audit reads. Off = php-api, on = hunkware-api (Bearer via customOptions.useNode); the wire shape
  // is identical either way. Its OWN flag, not the taxes epic's switch that the same page's tax-codes
  // read rides. ONE flag for the pricing-audits family (owner decision): the FLEX and ZONE audit
  // routes ride it as they migrate, so their callers ramp and roll back together with this one.
  // Docs: docs/migrations/location-pricing-audits/07-frontend-migration.md
  'HunkWare_Product_Pricing_Audits_Node_Migration',
  'HunkWare_Products_Valuation_Node_Migration',
  // Off = php-api, on = hunkware-api. Wire shape is identical either way. ONE flag for the quoting group (owner
  // decision): quote-products-common, quote duplicate and quote reassociate ramp and roll
  // back together. Docs: docs/migrations/quote-products-common/07-frontend-migration.md
  'HunkWare_Quoting_Estimation_Node_Migration',
  'Hunkware_Redirect_Inner_pages___Quotes',
  'HunkWare_Redirect_Inner_Pages_Hunkpay_Transactions',
  'Hunkware_Redirect_Inner_Pages_Leads',
  'Hunkware_Redirect_Inner_Pages_Message_Location',
  'Hunkware_Redirect_Inner_Pages_Pending_Request_Detail_Page',
  'HunkWare_Redirect_Legacy_Claims_To_New_Inner_Pages',
  'HunkWare_Redirect_To_Parent_Location_When_No_Access_To_Location',
  'HunkWare_ReportingV4',
  // CID-8050 — on: a stored md5 hash is refused as a login credential (php-api login.php and hunkware-api legacy login);
  // off = today's behaviour. Keyed on the user.
  'HunkWare_Revoke_Hash_Login_Process',
  'HunkWare_Sequence_Based_Campaigns',
  'HunkWare_Service_Coverage_And_Booking_Availability_Migration',
  'HunkWare_Services_List_v4_Migration',
  'HunkWare_States_Node_Migration',
  'HunkWare_Storage_Indicator_for_Moves',
  'HunkWare_Subpartner_Lookup_Node_Migration',
  'HunkWare_Tagging_System',
  // Group switch for the third-party-integration node-migration group (sendgrid webhook, google/cron/bookings,
  // mymove/leads, thinkific, …). Off = php-api, on = hunkware-api. The cron routes of the group read it, and
  // hunkware-ui's Learning Center launcher (/apps/thinkific) reads it to choose between php-api POST /thinkific/sync
  // and hunkware-api POST /v1/learning-center/sync; turning it on for ANY group member moves that launcher too. It
  // gates NOTHING on POST /sendgrid/webhook: a treatment cannot redirect a request that starts at the vendor, so that
  // route moves by an ALB exact-path rule. The BullMQ schedulers that replace external cron lines (mymove/leads
  // hourly, google/cron/bookings daily) evaluate it with the SERVER key, so turning it on for ANY group member arms
  // them in production — disarm the external schedules first (google-cron-bookings decision 11,
  // docs/migrations/mymove-leads/07-frontend-migration.md).
  // Docs: docs/migrations/thinkific-sync/07-frontend-migration.md,
  // docs/migrations/sendgrid-webhook/07-frontend-migration.md,
  // docs/migrations/google-cron-bookings/07-frontend-migration.md,
  // docs/migrations/mymove-leads/07-frontend-migration.md
  'HunkWare_Third_Party_Integration_Webhooks_Node_Migration',
  'HunkWare_User_Notifications_Devices_Profile_Media_Node_Migration',
  // Transport switch for the CID-7326 "Tokens, Search & Utilities" node-migration epic; first
  // caller: hunkware-ui met-abt's addonsettings GET. Off = php-api, on = hunkware-api.
  // Docs: docs/migrations/location-addon-settings/07-frontend-migration.md
  'HunkWare_Tokens_Search_Utilities_Node_Migration',
  'HunkWare_UserSettings_Display_NewJobPage',
  'HunkWare_Yelp_Node_Worker_Enabled',
  'HunkWare_Yelp_Node_Subscribe_Enabled',
  // Off = php-api, on = hunkware-api; the wire shape is identical either way. ONE switch for the
  // zones & territories family (owner decision): the surge-price-list reset and the
  // /locations/{id}/zones CRUD ride it together, so a rollback reverts both and the
  // first-enable blast radius is the union of their callers.
  // Docs: docs/migrations/location-surge-pricing-reset/07-frontend-migration.md
  //       docs/migrations/location-zones/07-frontend-migration.md
  'HunkWare_Zones_Territories_Node_Migration',
  'HWV4_JobPage_AuditLog',
  'HWV4_JobPage_CompletedActions',
  'HWV4_JobPage_ConvertToJobFromScheduleView',
  'HWV4_JobPage_ViewTaskNotes',
  'HWV4_Settings_Franchise',
  'Messaging_OnlyGetMessagesAfterXID',
  'MET_Give_Range_For_Inventory_Quote',
  'MET_JunkQuoteSupport',
  'NASA_AttachImageToQBInvoice',
  'NASA_PartnerSubpartner_ActiveFlag',
  // Read by the boards automations executor (docs/migrations/boards-automations-cron/04-data-inputs.md §Split).
  'NASA-AddLeadsToCallingQueue',
  'NASA-SaveSubPartnerInfoForLeads',
  'Online_Booking_Misleading_Target_Date',
  'Online_Booking_Generate_Node_Migrate',
  'Online_Booking_Clear_Abandoned_Reason_On_Completion',
  // booking-v4 Junk pricing step (CID-7739): on = truck cards, columns and Liquid price texts from Sanity, prices
  // keyed by product id; off = the built-in three cards matched by product name, as before. Per location.
  'Online_Booking_Sanity_Pricing_Cards',
  'OnlineBooking_AskForEmailOnConfirmation',
  'OnlineBooking_NoJunkPricingForCorporateLocations',
  'OnlineBooking_RedirectVersionJunk',
  'OnlineBooking_RedirectVersionLabor',
  'OnlineBooking_RedirectVersionMove',
  'OnlineBooking_TrackVersion',
  'OnlineBooking_TwilioSegmentTracking',
  'PartnerManagement_DefaultReferralSource',
  'PartnerManagement_HandleLeadsInSLC',
  'PartnerManagement_OnlineBooking_ConfigureCategories',
  'PartnerManagement_SetDefaultValues',
  'PartnerPortal_AddAttachments',
  'PartnerPortal_HideTypeOfItemsForMoving',
  'PartnerPortal_Partner_Specific_Promotions',
  'Payments_AllowPrepaymentRefunds',
  'Pricing_GlobalModifiers',
  'Pricing_Rules',
  'HunkWare_Node_Version_Calculate_Quote',
  // Quote-create migration (docs/migrations/quote-create/07-frontend-migration.md)
  // — ONE transport flag for all four callers (Jet create + Jet GET +
  // crew-app GET + MET/ABT GET), mirroring Calculate_Quote above. Flipping it
  // moves every caller at once; there is no per-caller soak and rollback is
  // all-or-nothing. Flip only AFTER gate 1 (doc 08).
  //
  // The name must match Split.io EXACTLY. A key that does not exist there
  // resolves to the `control` treatment, which `useFeatureFlags` maps to
  // `false` — identical to "off", so a typo cannot be told apart from a flag
  // that simply hasn't been turned on. Confirm the key in Split before
  // concluding a flip "didn't work".
  //
  // It is Save_Quote (the UI action), NOT Create_Quote — and it gates the
  // GET callers too, despite the name.
  'HunkWare_Node_Version_Save_Quote',
  // Runtime shadow guard for the quote-calc migration (docs/migrations/quote-calculation/09-runtime-shadow-guard.md).
  // Treatments: off | observe | guard, evaluated per location.
  'HunkWare_Quote_Calc_Shadow_Guard',
  'Quotes_PortToPort',
] as const;
